import { useState, useReducer, useEffect, useContext } from "react";
import {
  Collapse,
  Form,
  Button,
  Checkbox,
  Row,
  Col,
  Modal,
  message,
} from "antd";
import { HJCreateDelivery } from "@FORMS";
import {
  AiOutlinePlus,
  AiFillSave,
  AiOutlineCloudUpload,
} from "react-icons/ai";
import { FiTrash2 } from "react-icons/fi";
import UploadModal from "./upload-modal";
import cloneDeep from "lodash/cloneDeep";
import { OptionSelect } from "@MASTER";
import { AppContext, AuthContext } from "@CONTEXTS";

import "./index.scss";
import CreateResultModal from "./create-result-modal";
import PackageResultModal from "./package-result-modal";

const { Panel } = Collapse;
const initialState = {
  index: Date.now(),
  customerDeliveryNo: null,
  recipientName: null,
  recipientSurname: null,
  recipientPhone: null,
  recipientTown: null,
  recipientCity: null,
  deliveryType: null,
  checked: false,
  isValid: null,
  panelOpen: true,
};

const reducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case "SET_DATA":
      return payload.map((item) => ({
        ...item,
        checked: false,
        isValid: null,
        panelOpen: true,
      }));
    case "CHANGE_VALUES":
      return state.map((item, index) => {
        if (item) {
          const that = payload[index];
          return {
            // index değerinin sürekli artan olması lazım.
            // bir row silindiğinde, formdaki değeri kalıyor.
            index: item.index,
            customerDeliveryNo: that?.customerDeliveryNo,
            recipientName: that?.recipientName,
            recipientSurname: that?.recipientSurname,
            recipientPhone: that?.recipientPhone,
            recipientTown: that?.recipientTown,
            recipientCity: that?.recipientCity,
            deliveryType: that?.deliveryType,
            checked: item.checked,
            isValid: null,
            panelOpen: item.panelOpen,
          };
        }
      });
    case "ADD_NEW_ROW":
      // index değerinin sürekli artan olması lazım. index:2 silinmişse bile, yeni eklenen row 3'ten başlamalı.
      const cloned = cloneDeep(state);
      const newState = cloned.concat([{ ...initialState, index: Date.now() }]);
      return newState;

    case "SET_CHECKED":
      return state.map((item) => {
        return {
          ...item,
          checked: payload === item.index ? !item.checked : item.checked,
        };
      });
    case "SET_REMOVE":
      // her türlü en az 1 tane row kalması gerekiyor.
      const filterResult = state.filter((x) => !x.checked);
      if (filterResult.length === 0) {
        return [];
      }
      return filterResult;
    case "SET_PANEL_OPEN":
      return state.map((item) =>
        item.index === payload ? { ...item, panelOpen: !item.panelOpen } : item
      );

    case "SET_VALIDATION_ERROR":
      return state.map((item) => {
        if (payload.includes(item.index)) {
          return {
            ...item,
            isValid: false,
            panelOpen: true,
          };
        }
        return {
          ...item,
          isValid: true,
          panelOpen: false,
        };
      });
    case "SET_SELECT_ALL": {
      return state.map((item) => {
        return {
          ...item,
          checked: payload,
        };
      });
    }
    case "REMOVE_ALL": {
      return [];
    }
    default:
      break;
  }
};

const Delivery = () => {
  const [form] = Form.useForm();
  const [warehouseForm] = Form.useForm();
  const [state, dispatch] = useReducer(reducer, [initialState]);
  const [checkAll, setCheckAll] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [createResultModal, setCreateResultModal] = useState(false);
  const { options } = useContext(AppContext);
  const { info } = useContext(AuthContext);
  const [packageResultModal, setPackageResultModal] = useState(false);

  useEffect(() => {
    // eğer 1 tane depo varsa default olarak ilk sıradakini seçeceğiz.
    if (options["WAREHOUSES"]?.length === 1) {
      warehouseForm.setFieldsValue({ warehouse: options["WAREHOUSES"][0].key });
    }
  }, [options, warehouseForm]);

  const handleSave = async () => {
    warehouseForm.validateFields().then(async () => {
      const warehouse = warehouseForm.getFieldValue("warehouse");
      const result = await validateAllFields();
      if (result) {
        localStorage.removeItem("draftDeliveries");
        const arr = [];
        for (const item in result) {
          const that = result[item];
          arr.push({
            ...that,
            warehouse,
          });
        }
        if (info?.autoPackage) {
          //otomatik paketleme açıksa paketleme modalini açıyoruz.
          setPackageResultModal(arr);
        } else {
          setCreateResultModal(arr);
        }
      }
    });
  };

  const validateAllFields = async () => {
    try {
      const result = await form.validateFields();
      return result;
    } catch (e) {
      if (e.errorFields) {
        const validateArr = [];
        e.errorFields.forEach((item) => {
          const errorIndex = item.name[0];
          if (!validateArr.includes(errorIndex)) {
            validateArr.push(errorIndex);
          }
        });
        dispatch({ type: "SET_VALIDATION_ERROR", payload: validateArr });
      }

      return false;
    }
  };

  const handleInsertMany = (values) => {
    dispatch({ type: "REMOVE_ALL" });
    form.resetFields();
    const obj = {};
    const arr = [];
    values.forEach((item, i) => {
      const index = Date.now() - i;
      obj[index] = {
        index,
        customerDeliveryNo: item.customerDeliveryNo,
        deliveryType: item.deliveryType || "HX_STD",
        slot: item.slot || null,
        desi: item.desi,
        manuelDesi: item.manuelDesi,
        totalParcels: item.totalParcels || 1,
        recipientName: item.recipientName || null,
        recipientSurname: item.recipientSurname || null,
        recipientPhone: item.recipientPhone || null,
        recipientCity: item.recipientCity.name || null,
        recipientTown: item.recipientTown.name || null,
        recipientDistrict: item.recipientDistrict.name || null,
        recipientAddressLine: item.recipientAddressLine || null,
        receiverName: item.receiverName || null,
        receiverSurname: item.receiverSurname || null,
        receiverPhone: item.receiverPhone || null,
      };
      arr.push(obj[index]);
    });
    dispatch({ type: "SET_DATA", payload: arr });
    form.setFieldsValue(obj);
    setUploadModal(false);
  };

  if (uploadModal) {
    return (
      <UploadModal
        onClose={() => setUploadModal(false)}
        onUploadedData={(val) => {
          warehouseForm.setFieldsValue({ warehouse: val.warehouse });
          handleInsertMany(val.data);
        }}
      />
    );
  }

  return (
    <>
      {packageResultModal && (
        <PackageResultModal
          data={packageResultModal}
          onClose={() => {
            setPackageResultModal(false);
          }}
          onSuccessAndRemove={(customerDeliveryNoList) => {
            // customerDeliveryNo'lardan state indexlerini bulalım:
            const indexes = state
              .filter((x) =>
                customerDeliveryNoList.includes(x.customerDeliveryNo)
                  ? true
                  : false
              )
              .map((y) => y.index);

            // indexleri tek tek remove edelim
            indexes.forEach((index) =>
              dispatch({ type: "SET_CHECKED", payload: index })
            );

            dispatch({ type: "SET_REMOVE" });
            if (
              indexes.length > 0 &&
              indexes.length === customerDeliveryNoList.length
            ) {
              dispatch({ type: "ADD_NEW_ROW" });
            }
          }}
        />
      )}
      {createResultModal && (
        <CreateResultModal
          data={createResultModal}
          onClose={() => {
            setCreateResultModal(false);
          }}
          onSuccessAndRemove={(customerDeliveryNoList) => {
            // customerDeliveryNo'lardan state indexlerini bulalım:
            const indexes = state
              .filter((x) =>
                customerDeliveryNoList.includes(x.customerDeliveryNo)
                  ? true
                  : false
              )
              .map((y) => y.index);

            // indexleri tek tek remove edelim
            indexes.forEach((index) =>
              dispatch({ type: "SET_CHECKED", payload: index })
            );

            dispatch({ type: "SET_REMOVE" });
            if (
              indexes.length > 0 &&
              indexes.length === customerDeliveryNoList.length
            ) {
              dispatch({ type: "ADD_NEW_ROW" });
            }
          }}
        />
      )}
      <Row gutter={24}>
        <Col flex="none">
          <Checkbox
            style={{ marginLeft: 19, marginTop: 7 }}
            checked={checkAll}
            onClick={(e) => {
              dispatch({ type: "SET_SELECT_ALL", payload: e.target.checked });
              setCheckAll(e.target.checked);
            }}
          >
            Tümünü Seç
          </Checkbox>
        </Col>
        <Col flex="auto">
          <Row gutter={24}>
            <Col flex="auto">
              <Form form={warehouseForm}>
                <Form.Item
                  name="warehouse"
                  label={null}
                  rules={[
                    {
                      required: true,
                      message:
                        "Lütfen tüm gönderiler için çıkış deposunu seçiniz.",
                    },
                  ]}
                >
                  <OptionSelect
                    name="WAREHOUSES"
                    labelKey={
                      info?.specialTags?.includes("B2BPRO") ? "name" : "tr"
                    }
                    placeholder="Çıkış deposu seçiniz"
                    allowClear={false}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children?.toLowerCase() ?? "").includes(
                        input?.toLowerCase()
                      )
                    }
                    showSearch
                  />
                </Form.Item>
              </Form>
            </Col>
            <Col flex="none">
              <Button
                icon={<AiOutlineCloudUpload />}
                type="primary"
                onClick={() =>
                  info?.authorities?.includes("CREATE_EXCEL_DELIVERY")
                    ? setUploadModal(true)
                    : message.error(
                        "Excel ile gönderi yükleme yetkiniz bulunmamaktadır!"
                      )
                }
              >
                Excel ile Gönderi Yükle
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onBlur={() => {
          const values = form.getFieldsValue();
          const arr = [];
          for (const item in values) {
            arr.push(values[item]);
          }
          // validateAllFields();
          dispatch({
            type: "CHANGE_VALUES",
            payload: arr,
          });
        }}
      >
        <Collapse
          // key={i}
          // accordion
          key="collapse"
          //onChange={(e) => dispatch({ type: "SET_ACTIVE_PANELS", payload: e })}
          activeKey={state.filter((x) => x.panelOpen).map((y) => y.index)}
          expandIconPosition="right"
          collapsible="disabled"
          style={{
            backgroundColor: "#fff",
            border: "none",
          }}
        >
          {state.map((item, mapIndex) => {
            return (
              <Panel
                key={item.index}
                style={{ marginBottom: 12 }}
                className={
                  item.isValid == null
                    ? "panel-bg-color-none"
                    : item.isValid
                    ? "panel-bg-color-success"
                    : "panel-bg-color-error"
                }
                extra={
                  <Button
                    type="danger"
                    size="small"
                    icon={<FiTrash2 />}
                    onClick={() => {
                      if (item.customerDeliveryNo) {
                        // eğer deliveryNo yazmışsa önce onay alalım.
                        Modal.confirm({
                          title: "Onay",
                          content:
                            "Satırı kaldırmak istediğinizden emin misiniz?",
                          onOk: () => {
                            dispatch({
                              type: "SET_CHECKED",
                              payload: item.index,
                            });
                            setTimeout(() => {
                              dispatch({ type: "SET_REMOVE" });
                            }, 100);
                          },
                        });
                      } else {
                        // eğer satır boşsa hemen sil
                        dispatch({ type: "SET_CHECKED", payload: item.index });
                        setTimeout(() => {
                          dispatch({ type: "SET_REMOVE" });
                        }, 100);
                      }
                    }}
                  >
                    Kaldır
                  </Button>
                }
                header={
                  <div
                    style={{
                      width: "calc(100% - 120px)",
                      display: "inline-block",
                    }}
                  >
                    <Row gutter={24}>
                      <Col flex="none">
                        <Checkbox
                          checked={item.checked}
                          onChange={() =>
                            dispatch({
                              type: "SET_CHECKED",
                              payload: item.index,
                            })
                          }
                        />
                      </Col>
                      <Col flex="auto">
                        <div
                          className="panel-clickable-area-row"
                          onClick={() =>
                            dispatch({
                              type: "SET_PANEL_OPEN",
                              payload: item.index,
                            })
                          }
                        >
                          {item.customerDeliveryNo ? (
                            <Row>
                              <Col span={5}>{item.customerDeliveryNo}</Col>
                              <Col span={5}>
                                {item.recipientName} {item.recipientSurname}
                              </Col>
                              <Col span={4}>{item.recipientPhone}</Col>
                              <Col span={5}>
                                {item.recipientTown?.name && (
                                  <div>
                                    {item.recipientTown?.name} /{" "}
                                    {item.recipientCity?.name}
                                  </div>
                                )}
                              </Col>
                              <Col span={5}>{item.deliveryType}</Col>
                            </Row>
                          ) : (
                            `Gönderi ${mapIndex + 1}`
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                }
              >
                <HJCreateDelivery
                  fieldName={item.index}
                  form={form}
                  autoDeliveryNumber={info?.autoDeliveryNumber}
                />
              </Panel>
            );
          })}
        </Collapse>
        <div style={{ marginTop: 24, marginBottom: 24 }}>
          <Button
            type="dashed"
            size="large"
            block
            icon={<AiOutlinePlus />}
            onClick={() => {
              dispatch({ type: "ADD_NEW_ROW" });
            }}
          >
            Yeni Satır Ekle
          </Button>
        </div>
        <Row gutter={24}>
          <Col flex="auto">
            <Button
              type="danger"
              icon={<FiTrash2 />}
              disabled={
                state.filter((x) => x.checked).length === 0 ? true : false
              }
              onClick={() => {
                Modal.confirm({
                  title: "Onay",
                  content: `Seçilmiş ${
                    state.filter((x) => x.checked).length
                  } adet gönderiyi kaldırmak istediğinizden emin misiniz?`,
                  onOk: () => {
                    dispatch({ type: "SET_REMOVE" });
                  },
                });
              }}
            >
              Seçilenleri Listeden Kaldır
            </Button>
          </Col>
          <Col flex="none">
            <Button
              style={{ float: "right" }}
              type="primary"
              onClick={() => handleSave()}
              icon={<AiFillSave />}
            >
              Tüm Gönderileri Kaydet
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default Delivery;
